import { flexRender } from '@tanstack/react-table';
import { Search, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@avanta/shared/components/Pagination';
import { SortableTableHeader } from '@avanta/shared/components/SortableTableHeader';
import { TableEmptyContent } from '@avanta/shared/components/TableEmptyContent';
import { TableSkeleton } from '@avanta/shared/components/TableSkeleton';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avanta/shared/components/ui/Table';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type FilterValue } from '@avanta/shared/types/table';
import { cn } from '@avanta/shared/utils/cn';
import { Text } from '../Text';
import { MobileCardList } from './components/MobileCardList';
import { TableColumnFilter } from './components/TableColumnFilter';
import { TableFilterButtons } from './components/TableFilterButtons';
import { TableFilterDateRange } from './components/TableFilterDateRange';
import { paginatedTableFrameVariants } from './styles';
import { type PaginatedTableLayoutProps } from './types';
import { useAutoPageSize } from './useAutoPageSize';
import { useTableTruncation } from './useTableTruncation';

export const PaginatedTable = <T, K extends FilterValue = FilterValue>({
  table,
  columns,
  isLoading: isLoadingProp = false,
  emptyMessage = '',
  emptyTitle,
  title,
  subtitle,
  icon,
  tableClassName,
  withoutBorder = false,
  variant = 'default',
  withSearch = false,
  searchPlaceholder = 'Common.search',
  searchPlaceholderNs,
  actionSlot,
  onRowAction,
  filter,
  selectedFilterKey,
  setFilter,
  dateRangeFilter,
  selectedDateRange,
  setDateRange,
  pageSize,
  autoPageSize,
}: PaginatedTableLayoutProps<T, K>): React.ReactElement => {
  const { getHeaderGroups, getRowModel, getState, setGlobalFilter } = table;
  const rows = getRowModel().rows;
  const { isLoading: isTableLoading = false, isPageCountLoading = false } =
    table.options.meta ?? {};
  const isLoading = isLoadingProp || isTableLoading;
  const reservesPaginationSpace =
    Boolean(autoPageSize) && (table.getRowCount() > 0 || isTableLoading);
  const hasRows = rows.length > 0;
  const globalFilter = getState().globalFilter;
  const Icon = icon;
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const hasActiveSearch = typeof globalFilter === 'string' && globalFilter.trim().length > 0;
  const resolvedEmptyTitle = hasActiveSearch ? t('Common.noResultsFound') : emptyTitle;
  const resolvedEmptyMessage = hasActiveSearch ? '' : emptyMessage;
  const hasTruncatableColumns = table
    .getVisibleLeafColumns()
    .some(column => column.columnDef.meta?.truncation);
  const { containerRef, tableRef, isTruncated } = useTableTruncation(
    hasTruncatableColumns && hasRows && !isLoading
  );
  const [isAutoPageSize, setIsAutoPageSize] = useState(() => Boolean(autoPageSize));
  const { frameRef, footerRef } = useAutoPageSize({
    table,
    tableRef,
    config: autoPageSize,
    pageSize,
    isLoading,
    isActive: isAutoPageSize,
  });

  const handlePageSizeChange = (isAuto: boolean): void => {
    setIsAutoPageSize(Boolean(autoPageSize) && isAuto);
  };

  const showFilters = Boolean(filter && filter.options.length > 0 && setFilter);
  const showDateRangeFilter = Boolean(dateRangeFilter && setDateRange);

  const tableContent = (
    <div className="flex min-w-0 flex-col gap-6">
      {(withSearch || showFilters || showDateRangeFilter) && (
        <div className="flex flex-col-reverse gap-4 am-lg:flex-row am-lg:items-center am-lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
            {showFilters && filter && setFilter && (
              <TableFilterButtons
                options={filter.options}
                selectedKey={selectedFilterKey}
                onChange={setFilter}
                allLabel={filter.allLabel}
              />
            )}
            {showDateRangeFilter && dateRangeFilter && setDateRange && (
              <TableFilterDateRange value={selectedDateRange} onChange={setDateRange} />
            )}
          </div>
          {withSearch && (
            <div className="max-w-xs max-am-lg:max-w-full flex-1 flex items-center gap-2">
              <Input
                leftIcon={Search}
                rightSlot={
                  <Button
                    variant="ghost"
                    icon="only"
                    onClick={() => setGlobalFilter('')}
                    className={cn('hover:bg-transparent text-denim-900', !globalFilter && 'hidden')}
                  >
                    <XIcon className="size-6" />
                  </Button>
                }
                placeholder={t(
                  searchPlaceholder,
                  searchPlaceholderNs ? { ns: searchPlaceholderNs } : {}
                )}
                value={globalFilter}
                onChange={e => setGlobalFilter(e.target.value)}
                className="w-full shrink-0"
              />
            </div>
          )}
        </div>
      )}
      <div className="am-lg:hidden">
        <MobileCardList
          rows={rows}
          headerGroups={getHeaderGroups()}
          isLoading={isLoading}
          columnsCount={columns.length}
          emptyMessage={resolvedEmptyMessage}
          emptyTitle={resolvedEmptyTitle}
        />
        <Pagination
          table={table}
          autoPageSize={Boolean(autoPageSize)}
          isAutoPageSize={isAutoPageSize}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
          isPageCountLoading={isPageCountLoading}
        />
      </div>
      <div
        ref={frameRef}
        data-slot="paginated-table-frame"
        className={cn(paginatedTableFrameVariants({ variant, withoutBorder }))}
      >
        <div ref={containerRef} className="min-w-0 w-full">
          <Table ref={tableRef} className={cn('w-full min-w-full table-auto', tableClassName)}>
            <TableHeader>
              {getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHead key={header.id} className="last:w-px">
                      <TableColumnFilter column={header.column}>
                        <SortableTableHeader header={header} />
                      </TableColumnFilter>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableSkeleton columns={columns.length} rows={getState().pagination.pageSize} />
              ) : hasRows ? (
                rows.map(row => (
                  <TableRow
                    key={row.id}
                    className={cn(onRowAction && 'cursor-pointer')}
                    onClick={() => onRowAction?.(row.original)}
                  >
                    {row.getVisibleCells().map(cell => {
                      const truncation = cell.column.columnDef.meta?.truncation;
                      const view = flexRender(cell.column.columnDef.cell, cell.getContext());
                      return (
                        <TableCell key={cell.id} className="last:w-px">
                          {truncation ? (
                            <div
                              data-table-truncation=""
                              className={cn(
                                'block',
                                isTruncated && ['truncate', truncation.maxWidthClassName]
                              )}
                            >
                              {view}
                            </div>
                          ) : (
                            view
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-auto border-none text-center whitespace-normal group-hover:bg-transparent"
                  >
                    <TableEmptyContent
                      className="w-auto"
                      emptyTitle={resolvedEmptyTitle}
                      emptyMessage={resolvedEmptyMessage}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div ref={footerRef} data-slot="paginated-table-footer">
          {actionSlot ? (
            <div className="flex justify-end mt-2">{actionSlot}</div>
          ) : (
            <div className={cn(reservesPaginationSpace && 'flow-root min-h-11')}>
              <Pagination
                table={table}
                autoPageSize={Boolean(autoPageSize)}
                isAutoPageSize={isAutoPageSize}
                onPageSizeChange={handlePageSizeChange}
                isLoading={isLoading}
                isPageCountLoading={isPageCountLoading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {(title || subtitle || Icon) && (
        <div className="flex items-center gap-2">
          {Icon && <Icon className="size-8 shrink-0 text-denim-500" strokeWidth={1.5} />}
          {(title || subtitle) && (
            <div>
              {title && (
                <Text tag="h3" variant="heading-sm-medium">
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text tag="h3" variant="text-xs-regular" className="text-gray-400">
                  {subtitle}
                </Text>
              )}
            </div>
          )}
        </div>
      )}
      {tableContent}
    </div>
  );
};
